import React, {useState, useEffect} from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Box, Card, Divider, Grid2 as Grid, Link, Stack, Typography, useTheme } from '@mui/material';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

//import schedules
import ReportAmendReasonBlock from '../components/ReportAmendReasonBlock';
import { ExpenditureItem } from '../../../types/reports/expenditureItem';
import ExpendituresScheduleTable from '../components/ExpendituresScheduleTable';
import ReportAmendedReasonBlock from '../components/ReportAmendedReasonBlock';
import Cpf22ReportContent from '../../../types/reports/cpf22ReportContent';
import MiscChangeLogTable from '../components/MiscChangeLogTable';
import miscReportChangeLog from '../../../types/reports/miscReportChangeLog';

interface ReportProps {
    reportId: number;
    currentReport?: any
}

const Cpf22Report = (reportProps: ReportProps): JSX.Element => {

    const theme = useTheme();
    const { showBoundary } = useErrorBoundary();  
    const [scheduleValue, setScheduleValue] = useState('summary');
    const fieldWidth = { xs: "50%", sm: "40%", md: "40%" };

    const [expendituresRows, setExpendituresRows] = useState<ExpenditureItem[]>();
    const [report, setReport] = useState<Cpf22ReportContent>();
    const [reportChangeLogs, setReportChangeLogs] = useState<miscReportChangeLog[]>();

    useEffect(() => {
            if(reportProps.currentReport){
                let report = reportProps.currentReport;
                setScheduleValue('summary');
                setReport(report);
                setExpendituresRows(report.expenditures);
            }
            else{
                getFullReport();
            }
            getReportDiffs();
    }, [reportProps.reportId])

    const getFullReport = async () => {

        try{
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreport/${reportProps.reportId}`)
            .then(result => {
            if (!result.ok) {
                showBoundary(new Error('Failed to get CPF22 report content.'));
            }
            return result.json();
        })
        .then(result => {

            let report = result;
            setScheduleValue('summary');
            setReport(report);
            setExpendituresRows(report.expenditures);
            return;
        });
        }catch(error){
            console.error('Uncaught Error:', error);
        }
    }

    const getReportDiffs = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreport/diffs/${reportProps.reportId}`);
            if (!response.ok) {
                setReportChangeLogs(undefined);
                return;
            }
            const diffs = await response.json();
            setReportChangeLogs(Array.isArray(diffs) ? diffs : undefined);
        } catch (error) {
            console.error('Uncaught Error:', error);
        }
    }

  const handleScheduleChange = (event: React.SyntheticEvent, newValue: string) => {
    setScheduleValue(newValue);
  };

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <TabContext value={scheduleValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={handleScheduleChange} aria-label="periodic report schedules">
            <Tab className="report-tab-label" label="Summary" value="summary" />
            {
                (report?.expenditures?.length ?? 0) > 0 &&
                <Tab className="report-tab-label" label={`Expenditures [${report?.expenditures?.length}]`} value="expenditures" />
            }
            {
                 reportChangeLogs && reportChangeLogs.length > 0 &&
                 <Tab className="report-tab-label" label={`Change Log`} value="logs"/>
            }
          </TabList>
        </Box>
        <TabPanel value="summary">
            <Grid container spacing={2}>
                {/*Filer Info*/}
                <Grid size={12}>
                    <Box
                        component={Card}
                        boxShadow={0}
                        sx={{ bgcolor: 'alternate.light' }}
                        border={`1px solid ${theme.palette.divider}`}
                    >
                        <Box padding={{ xs: 2, sm: 3 }}>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                    Filer:
                                </Typography>
                                <Typography variant={'body1'} fontWeight={550}>
                                    <div dangerouslySetInnerHTML={{ __html: report?.filingEntityFull || '' }} />
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                     Filed On:
                                </Typography>
                                <Typography variant={'body1'} align='left' width={fieldWidth}>
                                     {report?.filingDate}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                    Reporting Period:
                                </Typography>
                                <Typography variant={'body1'} align='left' width={fieldWidth} color="primary">
                                     {report?.reportingPeriod}
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>
                </Grid>
                {/*Amendment Reason*/}
                {
                    report?.isAmendment && 
                    <ReportAmendReasonBlock reason={report?.amendmentReason} previousReportId={report?.amendmentToReportId} isIE={true}></ReportAmendReasonBlock>
                }
                {/*Being Amended*/}
                {
                    report && report.amendedByReportId>0 &&
                    <ReportAmendedReasonBlock nextReportId={report.amendedByReportId} isIE={true}></ReportAmendedReasonBlock>
                }
                {/*Financial Summary*/}
                <Grid size={12}>
                    <Box
                        component={Card}
                        boxShadow={0}
                        sx={{ bgcolor: 'alternate.light' }}
                        border={`1px solid ${theme.palette.divider}`}
                    >
                        <Box padding={{ xs: 2, sm: 3 }}>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Ballot Question:
                                </Typography>
                                <Typography variant={'body1'} align='right'>
                                     {report?.bqDisplay}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Position:
                                </Typography>
                                <Typography variant={'body1'} align='right'>
                                     {report?.position}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Total Expenditures this period:
                                </Typography>
                                <Typography variant={'body1'} align='right'>
                                     {report?.expenditureTotal}
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </TabPanel>
        <TabPanel value="expenditures">
            <ExpendituresScheduleTable tableRows={expendituresRows}></ExpendituresScheduleTable>
            <Box padding={{ xs: 2, sm: 3 }}>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'body1'}>
                        Total Expenditures:
                    </Typography>
                    <Typography variant={'body1'} fontWeight={550} align='right'>
                        {report?.expenditureTotal}
                    </Typography>
                </Stack>
            </Box>
        </TabPanel>
        <TabPanel value="logs">
            <MiscChangeLogTable changes={reportChangeLogs}></MiscChangeLogTable>
        </TabPanel>
      </TabContext>
    </Box>
  );
}

export default Cpf22Report;